import { useState, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import PromptService from '~services/prompt';

import * as REST from '~services/rest';
import NotificationsActions from '~redux/notifications';

import { ChatContext } from './context';

export const useChat = ({ perPage = 10 }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { currentUser, recipient, updateDiscussion, discussion, messages, updateMessages } = useContext(ChatContext);
  const { getAccessTokenSilently } = useAuth0();

  const [offset, setOffset] = useState(0);
  const [seenByPeerAt, setSeenByPeerAt] = useState(null);
  const [scrollTo, setScrollTo] = useState(null);
  const [forceScroll, setForceScroll] = useState(null);

  const [shouldLoadNewMessages, setShouldLoadNewMessages] = useState(0);
  const [sentMessages, setSentMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);

  const parseMessage = (msg) => {
    return {
      messageId: msg.id,
      personId: msg.author_id,
      createdAt: msg.sent_timestamp,
      isSeen: msg.seen,
      body: msg.content,
      sentAt: msg.sent_timestamp,
    };
  };

  const markAsSeen = async () => {
    if (!discussion.discussionId) {
      return null;
    }

    const token = await getAccessTokenSilently();
    try {
      const result = await REST.post({
        name: `chats/${discussion.discussionId}`,
        mockFileName: 'chats/post-as-seen',
        requiresAuthentication: true,
        shouldThrowError: true,
        params: {
          action: 'seen',
          who: currentUser.personId,
          receiver: recipient.personId,
        },
        token,
      });

      return !!result;
    } catch (err) {
      // Fire-and-forget: a failed mark-as-seen (e.g. 404 on a stale chat) must never surface
      // a server-error popup.
      return false;
    }
  };

  useEffect(async () => {
    // console.log('discussion in useChat',discussion);
    if (!discussion.discussionId) {
      setIsLoadingMessages(false);
      return;
    }

    if (discussion.acceptanceState == 'rejected') {
      setIsLoadingMessages(false);
      return;
    }

    const token = await getAccessTokenSilently();
    let result;
    try {
      result = await REST.get({
        name: `chats/${discussion.discussionId}/messages`,
        mockFileName: 'chats/get-messages',
        token,
        shouldThrowError: true,
        // @TODO add pagination
        // params: {
        //   limit: perPage,
        //   offset,
        // },
      });
    } catch (err) {
      // A deleted/stale chat returns 404 - show an empty conversation instead of the generic
      // server-error popup. Anything else is a real failure, so surface it explicitly:
      // shouldThrowError bypasses the automatic popup, and a throw from this async effect
      // would be swallowed as an unhandled rejection.
      setIsLoadingMessages(false);
      if (err?.response?.status !== 404) {
        REST.showServerError(err);
      }
      return;
    }
    if (!result?.data || result.data.length === 0) {
      setIsLoadingMessages(false);
      return;
    }

    const parsedMessages = result.data.map((m) => parseMessage(m));
    setScrollTo('top');
    // const uniqueById = uniqByProp('messageId');
    // const unifiedArray = uniqueById([...messages, ...parsedMessages]);
    // const sortedMessages = unifiedArray.sort((a, b) => b.createdAt - a.createdAt);

    // @Todo - refactor and would be good to change logic to know when participant actually seen the messages
    const recipientMessage = parsedMessages.find((m) => m.personId === recipient.personId);
    if (recipientMessage && recipientMessage.sentAt) {
      setSeenByPeerAt(recipientMessage.sentAt);
    }

    updateMessages(parsedMessages);
    markAsSeen(discussion.discussionId);
    setIsLoadingMessages(false);
  }, [offset]);

  useEffect(async () => {
    if (!shouldLoadNewMessages) {
      return;
    }

    const token = await getAccessTokenSilently();
    let result;
    try {
      result = await REST.get({
        name: `chats/${discussion.discussionId}/messages`,
        mockFileName: 'chats/get-messages',
        token,
        shouldThrowError: true,
        // @TODO add pagination
        // params: {
        //   limit: perPage,
        //   offset,
        // },
      });
    } catch (err) {
      // A deleted/stale chat returns 404 - stop polling quietly instead of the generic
      // server-error popup. Anything else is a real failure, so surface it explicitly:
      // shouldThrowError bypasses the automatic popup, and a throw from this async effect
      // would be swallowed as an unhandled rejection.
      setShouldLoadNewMessages(false);
      if (err?.response?.status !== 404) {
        REST.showServerError(err);
      }
      return;
    }

    if (!result || !result?.data || result.data.length === 0) {
      setShouldLoadNewMessages(false);
      return;
    }

    const parsedMessages = result.data.map((m) => parseMessage(m));
    setScrollTo('top');

    // For now we dont have pagination so we dont need to sort / unify messages;
    // const uniqueById = uniqByProp('messageId');
    // const unifiedMessages = uniqueById([...parsedMessages, ...messages]);
    // updateMessages(unifiedMessages);

    updateMessages(parsedMessages);

    // @Todo - refactor and would be good to change logic to know when participant actually seen the messages
    const recipientMessage = parsedMessages.find((m) => m.personId === recipient.personId);
    if (recipientMessage && recipientMessage.sentAt) {
      setSeenByPeerAt(recipientMessage.sentAt);
    }

    markAsSeen(discussion.discussionId);
    setShouldLoadNewMessages(false);
  }, [shouldLoadNewMessages]);

  useEffect(() => {
    if (!sentMessages.length) {
      return;
    }
    const nMessages = messages.map((m) =>
      sentMessages.indexOf(m.messageId) >= 0 ? { ...m, isDelivered: true, isSending: false } : m
    );
    setScrollTo(null);
    setForceScroll(null);
    updateMessages(nMessages);
    setSentMessages([]); 
  }, [sentMessages]);

  const handleSendMessageError = (messageId, errorMessage) => {
    updateMessages((prevMessages) =>
      prevMessages.map((m) => (m.messageId === messageId ? { ...m, isDelivered: false, isSending: false } : m))
    );

    dispatch(NotificationsActions.addNotification(errorMessage));
  };

  const sendMessage = async (messageBody) => {
    if (discussion.acceptanceState == 'rejected') {
        const warning = await PromptService.confirm({
            title: intl.formatMessage({ id: 'containers.discussion.chat-request.title', defaultMessage: 'You Can\'t do Chat' }),
            content: 'Chat has been rejected',
            className: 'c-modal__container',
            });
            history.replace('/chats');
      return;
    }
    discussion.acceptanceState = discussion.acceptanceState == undefined ? 'pending' : discussion.acceptanceState;
    setForceScroll('bottom');
    const messageId = uuidv4();
    updateMessages([
      ...messages,
      {
        messageId,
        personId: currentUser.personId,
        createdAt: moment().unix(),
        isDelivered: false,
        isSending: true,
        body: messageBody,
      },
    ]);

    const token = await getAccessTokenSilently();
    const result = await REST.post({
      name: `chats/messages`,
      requiresAuthentication: true,
      mockFileName: 'chats/post-message',
      token,
      params: {
        message: {
          id: messageId,
          chat_id: discussion.discussionId || undefined,
          author_id: currentUser.personId,
          receiver_id: recipient.personId,
          content: messageBody,
        },
        is_new_chat: messages.length === 0,
      },
    });

    if (result && result.status_msg && result.status_msg === 'Chat blocked.') {
      handleSendMessageError(
        messageId,
        intl.formatMessage({
          id: `containers.chat.options.block.notification.error`,
          defaultMessage: 'Cannot send message. Chat blocked.',
        })
      );
      return;
    }

    if (!result || (result && result.status_msg && result.status_msg !== 'OK')) {
      handleSendMessageError(
        messageId,
        intl.formatMessage({
          id: `containers.chat.send-message-error`,
          defaultMessage: 'Error - Cannot send message.',
        })
      );
      return;
    }

    if (!discussion.discussionId && result && result.data && result.data[0] && result.data[0].chat_id) {
      const newDiscussion = { ...discussion };
      newDiscussion.discussionId = result.data[0].chat_id;
      updateDiscussion(newDiscussion);
    }

    setSentMessages((prevSent) => [...prevSent, messageId]);
  };

  const handleSeenByPerson = (payload) => {
    // We want to update seenByPerson only for this discussion and if
    // seen by other person, not current user
    if (
      discussion.discussionId !== payload.chat_id ||
      !payload.seen_at ||
      !payload.participant_id ||
      payload.participant_id === currentUser.personId
    ) {
      return;
    }

    if (payload.seen_at > seenByPeerAt) {
      setSeenByPeerAt(payload.seen_at);
    }
  };

  const loadMoreOldMessages = () => {
    if (!isLoadingMessages && discussion.hasNextPage) {
      setOffset(offset + perPage);
    }
  };

  return {
    isLoadingMessages,
    markAsSeen,
    sendMessage,
    fetchNewMessages: () => setShouldLoadNewMessages(Date.now()),
    loadMoreOldMessages,
    scrollTo,
    forceScroll,
    seenByPeerAt,
    handleSeenByPerson,
  };
};
